import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ---- SHARED FILTER LOGIC (single source of truth, IST timezone) ----

// Date in IST (Asia/Kolkata) as YYYY-MM-DD, regardless of browser/server timezone
export const istDate = (d = new Date()) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// Convert any stored date/datetime string to its IST calendar date (YYYY-MM-DD)
export function toISTDay(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr);
  if (s.length <= 10 || !s.includes("T")) return s.slice(0, 10); // date-only: already a calendar date
  const d = new Date(s);
  if (isNaN(d)) return s.slice(0, 10);
  return istDate(d);
}

export function presetRange(preset) {
  const now = new Date();
  const today = istDate(now);
  const [yy, mm, dd] = today.split("-").map(Number);
  if (preset === "today") return { from: today, to: today };
  if (preset === "yesterday") {
    const d = istDate(new Date(now.getTime() - 86400000));
    return { from: d, to: d };
  }
  if (preset === "last7") {
    const d = istDate(new Date(now.getTime() - 6 * 86400000));
    return { from: d, to: today };
  }
  if (preset === "week") {
    const ref = new Date(Date.UTC(yy, mm - 1, dd));
    ref.setUTCDate(ref.getUTCDate() - ((ref.getUTCDay() + 6) % 7));
    return { from: ref.toISOString().slice(0, 10), to: today };
  }
  if (preset === "month") return { from: today.slice(0, 8) + "01", to: today };
  if (preset === "lastmonth") {
    const first = new Date(Date.UTC(yy, mm - 2, 1));
    const last = new Date(Date.UTC(yy, mm - 1, 0));
    return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
  }
  return { from: "", to: "" }; // all time
}

// Start AND end dates inclusive, compared on IST calendar days
export function inPeriod(dateStr, range) {
  if (!range?.from && !range?.to) return true;
  if (!dateStr) return false;
  const d = toISTDay(dateStr);
  if (range.from && d < range.from) return false;
  if (range.to && d > range.to) return false;
  return true;
}

// hrSel: array of hr ids; empty array = All HR
export const matchesHR = (recruiterId, hrSel) =>
  !hrSel || hrSel === "all" || (Array.isArray(hrSel) ? (hrSel.length === 0 || hrSel.includes(recruiterId)) : recruiterId === hrSel);

// HR Master dropdown source — dynamic, includes inactive HRs so their old data stays reachable
export function useHROptions(enabled = true) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/hr-options").then((r) => setOptions(r.data)).catch(() => {});
  }, [enabled]);
  return options;
}

export const PeriodFilter = ({ preset, onPreset, range, onRange, testId = "period-filter" }) => (
  <div className="flex flex-wrap items-end gap-2">
    <div className="w-36">
      <Label className="label-eyebrow">Period</Label>
      <Select value={preset} onValueChange={(v) => { onPreset(v); if (v !== "custom") onRange(presetRange(v)); }}>
        <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All time</SelectItem>
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="last7">Last 7 Days</SelectItem>
          <SelectItem value="week">This Week</SelectItem>
          <SelectItem value="month">This Month</SelectItem>
          <SelectItem value="lastmonth">Last Month</SelectItem>
          <SelectItem value="custom">Custom range</SelectItem>
        </SelectContent>
      </Select>
    </div>
    {preset === "custom" && (
      <>
        <div>
          <Label className="label-eyebrow">From</Label>
          <Input type="date" value={range.from} onChange={(e) => onRange({ ...range, from: e.target.value })} className="mt-1.5 h-9 w-40 bg-white" data-testid={`${testId}-from`} />
        </div>
        <div>
          <Label className="label-eyebrow">To</Label>
          <Input type="date" value={range.to} onChange={(e) => onRange({ ...range, to: e.target.value })} className="mt-1.5 h-9 w-40 bg-white" data-testid={`${testId}-to`} />
        </div>
      </>
    )}
  </div>
);

export const HRFilter = ({ value, onChange, users, testId = "hr-filter" }) => (
  <div className="w-44">
    <Label className="label-eyebrow">HR</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All HR</SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.name}{u.active === false ? " (inactive)" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// Multi-select HR filter with checkboxes. value = array of ids; [] = All HR
export const HRMultiFilter = ({ value, onChange, users, testId = "hr-multi-filter" }) => {
  const label =
    value.length === 0 ? "All HR"
      : value.length === 1 ? (users.find((u) => u.id === value[0])?.name || "1 HR")
        : `${value.length} HRs selected`;
  return (
    <div className="w-48">
      <Label className="label-eyebrow">HR Name</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="mt-1.5 h-9 w-full justify-between bg-white font-normal" data-testid={testId}>
            <span className="truncate">{label}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-2" align="start">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm font-medium" data-testid={`${testId}-all`}>
            <Checkbox checked={value.length === 0} onCheckedChange={() => onChange([])} />
            All HR
          </label>
          <div className="h-px bg-slate-100 my-1" />
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`${testId}-opt-${u.id}`}>
              <Checkbox
                checked={value.includes(u.id)}
                onCheckedChange={(ck) => onChange(ck ? [...value, u.id] : value.filter((x) => x !== u.id))}
              />
              {u.name}
            </label>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PeriodFilter;
